/**
 * Safely read and JSON.parse a localStorage value.
 *
 * localStorage can hold the literal string "undefined": when a value is persisted
 * via `localStorage.setItem(key, JSON.stringify(undefined))`, `JSON.stringify(undefined)`
 * returns the JS value `undefined`, which `setItem` coerces to the string "undefined".
 * A bare truthy check then passes that string to `JSON.parse`, which throws
 * `SyntaxError: "undefined" is not valid JSON` — crashing a context provider during
 * render (above the ErrorBoundary, white-screening the app). This helper guards the
 * read and falls back safely on a missing, "undefined", or corrupt value.
 *
 * @param {string} key - localStorage key to read.
 * @param {*} fallback - value returned when the key is absent, "undefined", or unparseable.
 * @returns {*} the parsed value, or `fallback`.
 */
export const safeParseLocalStorage = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === "undefined") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    // Corrupt value (not valid JSON). Fall back rather than crash; warn so the
    // bad state is diagnosable instead of silently swallowed.
    console.warn(`safeParseLocalStorage: corrupt value for "${key}", using fallback`, raw);
    return fallback;
  }
};
